/**
 * nav-manifest.ts
 * Shared Zod schemas for navigation manifest contracts — FS-24.1
 *
 * Lives in @suite/contracts-kernel so it can be imported by both:
 *   - apps/frontend (nav filtering, SidebarNav component)
 *   - future: apps/backend (if SSR/server-side nav is needed)
 *
 * Note: apps/frontend does NOT depend on @suite/kernel (only @suite/contracts-*).
 *       Putting this here keeps the boundary correct — no Hono/Drizzle imports allowed.
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 *
 * NavItem: a single navigation entry (route link), optionally with children.
 * ModuleNavManifest: the full nav declaration for one product module.
 *   - moduleKey: matches the entitlement key ('locx', 'requi', etc.)
 *   - requiredEntitlementFlag: the catalog.feature_flag name that gates this module
 *   - items: top-level nav entries (may have children for collapsible sections)
 *
 * Spec: FS-24.1 / design §9 Phase 1a
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// NavItem — a single navigation entry
//
// Recursive type: children?: NavItem[]. We use z.lazy to handle the recursion.
// TypeScript needs an explicit interface + cast to avoid inference issues with
// circular Zod type annotations.
// ---------------------------------------------------------------------------

/**
 * A single navigation item shown in the sidebar.
 *
 * - itemKey: stable machine-readable identifier (e.g. "locx.orders"). Required
 *   on every manifest item (NAV-1.1). Must not be renamed after first publish
 *   without a DB migration on nav_item_config.item_key.
 * - label: display text (may change freely — itemKey is the stable key).
 * - route: Vue Router path (string — infra-free contract)
 * - requiredCapability: optional capability string OR array of capability
 *   strings; the user must hold at least ONE of them (via hasCapability glob
 *   semantics, AZ-2 / FS-23 — any-of, not all-of) to see the item. Widened
 *   from a single string (2026-08-31-requi-aprobacion-oc-por-monto, design.md
 *   D7) so an item can be reachable by more than one capability — e.g.
 *   `/compras/aprobaciones` must stay visible to a tier-1-only
 *   `requi.comprador` (`requi.oc.aprobar`) AND a tier-2-only
 *   `requi.aprobador` (`requi.oc.aprobar-monto`), neither of whom holds the
 *   pre-existing `requi.caso.aprobar`. A plain string is unaffected — it's
 *   just the one-element case of the same any-of check.
 *   NOTE: this is evaluated only on LEAF items. On a parent (an item WITH
 *   children) it is ignored — a section's visibility is driven entirely by
 *   whether any child survives filtering (empty-parent pruning). Gate a whole
 *   section by putting requiredCapability on its children, not the parent.
 * - children: optional nested items. If ALL children are filtered out, the
 *   parent section is pruned (empty-parent pruning).
 */
export interface NavItem {
  itemKey: string;
  label: string;
  route: string;
  requiredCapability?: string | readonly string[] | undefined;
  children?: NavItem[] | undefined;
}

// Use z.lazy for the recursive definition.
// Cast via z.ZodType<NavItem> to satisfy TypeScript's circular type check.
export const NavItemSchema: z.ZodType<NavItem> = z.lazy(() =>
  z.object({
    itemKey: z.string().min(1),
    label: z.string(),
    route: z.string(),
    requiredCapability: z.union([z.string(), z.array(z.string())]).optional(),
    children: z.array(NavItemSchema).optional(),
  }),
) as z.ZodType<NavItem>;

// ---------------------------------------------------------------------------
// ModuleNavManifest — navigation declaration for one product module
// ---------------------------------------------------------------------------

/**
 * The complete navigation manifest for a single product module.
 *
 * Registered in apps/frontend/src/modules/nav/registry.ts.
 * Consumed by filterNavForUser() to produce the user's visible nav tree.
 *
 * - moduleKey: 'locx' | 'requi' | 'nomi' | 'sumi' | 'app' | 'admin'
 *   Must match the key used in the entitledModules array from GET /auth/me.
 * - requiredEntitlementFlag: catalog.feature_flag name for this module.
 *   The module is gated: if entitledModules does not include moduleKey, ALL items hidden.
 * - items: top-level navigation entries for this module.
 */
export const ModuleNavManifestSchema = z.object({
  moduleKey: z.string(),
  requiredEntitlementFlag: z.string(),
  items: z.array(NavItemSchema),
});

export type ModuleNavManifest = z.infer<typeof ModuleNavManifestSchema>;
